import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { Locale } from '@front/i18n';

import { del, get, post, put } from '../api/requester';
import { DeviceDetails, OAuthLoginData, SignupUser, User, UserEmail } from '../types/commons';
import ApiError from '../api/api-error';
import { Country } from '../types/country';
import { formatLocaleToPreciseLocale } from '../utils/locale';

export type CreateUserResult = {
  user: User;
  token: string;
};

export const USERS_QUERY_KEY = ['users'];

const api = {
  get: () => get('/user'),
  create: (value: SignupUser) => post('/user', { skipJsonParsing: true, body: { user: value } }),
  update: (value: Partial<User & DeviceDetails>) => put('/user', { body: { user: value } }),
  delete: () => del('/user'),
  emailExists: (value: UserEmail) => post('/user/email_exists', { body: { user: value } }),
  setPreferredLanguage: ({ locale, country }: { locale: Locale; country: Country }) =>
    put('/user/preferred_language', { body: { locale: formatLocaleToPreciseLocale({ locale, country }) } }),
  oauthConnect: (authData: OAuthLoginData, userData?: SignupUser) =>
    post('/user/create_with_token', {
      skipJsonParsing: true,
      body: {
        omniauth: {
          provider: authData.provider,
          access_token: authData.accessToken,
          uid: authData.userID,
        },
        user: userData,
      },
    }),
};

const getUserWithToken = async (result: any): Promise<CreateUserResult> => {
  const userRes = await result.json();
  const token = result.headers.get('authorization');
  return { user: userRes, token };
};

export const useGetUser = (
  options?: Omit<UseQueryOptions<User, Error>, 'queryKey' | 'queryFn' | 'cacheTime' | 'staleTime'>,
) => {
  const queryClient = useQueryClient();
  return useQuery<User, Error>({
    queryKey: USERS_QUERY_KEY,
    queryFn: api.get,
    retry: (failureCount, error) => {
      // By default react query retries 3 times when we have an error. For this particular api call, if we have a 401
      // error, it is something normal and we do not want to retry the call in this case
      if (error instanceof ApiError && error.response.status === 401) return false;
      // otherwise, restore default
      const defaultRetry = queryClient.getDefaultOptions().queries?.retry;
      return Number.isSafeInteger(defaultRetry) ? failureCount < Number(defaultRetry) : false;
    },
    ...options,
    // We may have multiple components in the tree that need to show the user but we want to fetch it only once
    // Here we define a stale time so the data is considered not stale during the rendering of the page but not after
    // The value here is a guess on what is the best value for that
    staleTime: 1000,
  });
};

export const useCreateUser = (
  options?: Omit<UseMutationOptions<CreateUserResult, Error, SignupUser>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options || {};
  return useMutation<CreateUserResult, Error, SignupUser>({
    mutationFn: async (user: SignupUser) => getUserWithToken(await api.create(user)),
    onSuccess: async (response, variables, context) => {
      queryClient.setQueryData(USERS_QUERY_KEY, response.user);
      if (onSuccess) await onSuccess(response, variables, context);
    },
    ...otherOptions,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation<User, Error, Partial<User>>({
    mutationFn: (user: Partial<User>) => api.update(user),
    onSuccess: async (response) => {
      queryClient.setQueryData(USERS_QUERY_KEY, response);
    },
  });
};

export type OauthConnectParams = { omniAuthData: OAuthLoginData; userData?: SignupUser };

export const useOauthConnectUser = (
  options?: Omit<UseMutationOptions<CreateUserResult, Error, OauthConnectParams>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options || {};
  return useMutation<CreateUserResult, Error, OauthConnectParams>({
    mutationFn: async ({ omniAuthData, userData }: OauthConnectParams) =>
      getUserWithToken(await api.oauthConnect(omniAuthData, userData)),
    onSuccess: async (response, variables, context) => {
      queryClient.setQueryData(USERS_QUERY_KEY, response.user);
      if (onSuccess) await onSuccess(response, variables, context);
    },
    ...otherOptions,
  });
};

export const useUserEmailExists = () =>
  useMutation<boolean, Error, User['email']>({
    mutationFn: async (email: User['email']) => {
      try {
        await api.emailExists({ email });
        return false;
      } catch (e) {
        return true;
      }
    },
  });

export const useUpdatePreferredLanguageMutation = () => {
  const queryClient = useQueryClient();
  const { mutate: updatePreferredLanguage } = useMutation<User, ApiError, { locale: Locale; country: Country }>({
    mutationFn: async ({ locale, country }: { locale: Locale; country: Country }) =>
      api.setPreferredLanguage({ locale, country }),
    onSuccess: () => {
      queryClient.invalidateQueries(USERS_QUERY_KEY);
    },
  });

  return { updatePreferredLanguage };
};
